// src/hooks/useAssetManagement.js
import { useCallback } from 'react';
import { useReactFlow } from 'reactflow';
import api from '../api';
import localCacheService from '../services/localCacheService';
import { getRandomTextBg, normalizeAsset, calculateLongSide120 } from '../editorUtils';

/**
 * 素材管理：添加到图板、删除、保存、上传/下载公共库
 *
 * @param {object} deps
 * @param {Function} deps.setNodes
 * @param {Function} deps.setEdges        - setFlowEdges
 * @param {Function} deps.setGlobalAssets
 * @param {Function} deps.setPublicAssets
 * @param {Function} deps.setGlobalSpells
 * @param {Function} deps.setAlertMsg
 * @param {Function} deps.setDrawerOpen
 * @param {Function} deps.setActiveTab
 * @param {Function} deps.setSaveModalData
 * @param {Function} deps.setMenuData
 * @param {Function} deps.setContextMenu
 * @param {object}   deps.globalAssetsRef
 * @param {object}   deps.publicAssetsRef
 * @param {object}   deps.globalSpellsRef
 * @param {object}   deps.crewIdRef
 * @param {object}   deps.crewNameRef
 * @param {string}   deps.projectId
 * @param {Function} deps.handleUpdateNode
 * @param {Function} deps.handleDeleteNodeButton
 * @param {Function} deps.handleDetachImage
 * @param {Function} deps.handleReuploadLogic
 * @param {Function} deps.handleReplace
 * @param {Function} deps.handleGenerate
 * @param {Function} deps.handleRemoveRefFile
 * @param {Function} deps.handleRemoveRefText
 * @param {Function} deps.handleRemoveRefAudio
 * @param {Function} deps.revokeSecureBlob
 * @param {Function} deps.getSmartSpawnPosition
 * @param {Function} deps.onSlashAssetSpawn
 * @param {object}   deps.callbacksRef
 * @param {object}   deps.saveModalData
 */
const useAssetManagement = (deps) => {
  const {
    setNodes, setEdges: setFlowEdges, setGlobalAssets, setPublicAssets, setGlobalSpells,
    setAlertMsg, setDrawerOpen, setActiveTab, setSaveModalData, setMenuData, setContextMenu,
    globalAssetsRef, publicAssetsRef, globalSpellsRef, crewIdRef, crewNameRef,
    projectId,
    handleUpdateNode, handleDeleteNodeButton, handleDetachImage, handleReuploadLogic,
    handleReplace, handleGenerate, handleRemoveRefFile, handleRemoveRefText, handleRemoveRefAudio,
    revokeSecureBlob, getSmartSpawnPosition, onSlashAssetSpawn, callbacksRef, saveModalData,
  } = deps;

  const { getNodes, screenToFlowPosition } = useReactFlow();

  const getTextContent = useCallback((asset) => {
    if (!asset || asset.type !== 'text') return '';
    return String(asset.text || asset.data?.text || asset.url || '').trim();
  }, []);

  const prependUniqueAsset = useCallback((prev, incoming) => {
    const incomingId = String(incoming?.id ?? '');
    const incomingText = getTextContent(incoming);

    const filtered = (Array.isArray(prev) ? prev : []).filter((item) => {
      if (String(item?.id ?? '') === incomingId) return false;
      if (incoming?.type === 'text' && item?.type === 'text' && incomingText) {
        const itemText = getTextContent(item);
        if (itemText === incomingText) return false;
      }
      if (incoming?.type === 'workflow' && item?.type === 'workflow') {
        if (incoming?.title && incoming.title === item?.title) return false;
      }
      return true;
    });

    return [incoming, ...filtered];
  }, [getTextContent]);

  // --- 从后台拖拽到图板 ---
  const handleSpawnAsset = useCallback((asset) => {
    // 工作流资产
    if (asset.type === 'workflow' && asset.data) {
      const { nodes, edges } = asset.data;
      if (!nodes || nodes.length === 0) return;

      // 计算工作流节点群组包围盒，将整体定位到右侧抽屉左侧 100px、垂直居中
      const drawerWidth = 320;
      const nodeWidth = 280; // 节点默认宽度估算
      let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
      nodes.forEach(n => {
        const w = n.data?.customSize?.width || nodeWidth;
        const h = n.data?.customSize?.height || 200;
        if (n.position.x < minX) minX = n.position.x;
        if (n.position.x + w > maxX) maxX = n.position.x + w;
        if (n.position.y < minY) minY = n.position.y;
        if (n.position.y + h > maxY) maxY = n.position.y + h;
      });
      // 目标：群组最右边 = 抽屉左侧 100px 处
      const targetRight = screenToFlowPosition({ x: window.innerWidth - drawerWidth - 100, y: 0 });
      const targetCenterY = screenToFlowPosition({ x: 0, y: window.innerHeight / 2 });
      const groupWidth = maxX - minX;
      const groupHeight = maxY - minY;
      const offsetX = targetRight.x - groupWidth - minX;
      const offsetY = targetCenterY.y - groupHeight / 2 - minY;

      const idMap = new Map();
      const newNodes = nodes.map(n => {
        const newId = `node-wf-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
        idMap.set(n.id, newId);
        // 🛡️ 清理跨会话失效的 blob/local-cache URL
        const { localCacheUrl, uploading, annotationPreview, ...cleanNodeData } = n.data || {};
        // 清理无效的 output：blob/data URL、以及非合法协议的值（如裸 UUID）
        const isValidOutputUrl = (u) => {
          if (!u) return false;
          if (u.startsWith('http') || u.startsWith('local-cache://') || u.startsWith('/')) return true;
          if (u.startsWith('uploads/') || u.startsWith('thumbnails/') || u.includes('/uploads/') || u.includes('/thumbnails/')) return true;
          return false;
        };
        if (cleanNodeData.output && !isValidOutputUrl(cleanNodeData.output)) {
          cleanNodeData.output = undefined;
        }
        // 清理 outputs 数组中的无效 URL
        if (Array.isArray(cleanNodeData.outputs)) {
          cleanNodeData.outputs = cleanNodeData.outputs.filter(u => isValidOutputUrl(u));
          if (cleanNodeData.outputs.length === 0) cleanNodeData.outputs = undefined;
        }
        return {
          ...n, id: newId,
          position: { x: n.position.x + offsetX, y: n.position.y + offsetY },
          selected: true,
          data: {
            ...cleanNodeData, id: newId,
            // 🔒 加密工作流：继承 isSystemSpell 标记
            isSystemSpell: !!asset.isEncrypted,
            onUpdate: handleUpdateNode, onDelete: handleDeleteNodeButton,
            onDetach: handleDetachImage, onDownscaleInPlace: (...args) => callbacksRef?.current?.onDownscaleInPlace?.(...args), onReupload: handleReuploadLogic,
            onReplace: handleReplace, onGenerate: handleGenerate,
            onRemoveRefFile: handleRemoveRefFile, onRemoveRefText: handleRemoveRefText,
            onRemoveRefAudio: handleRemoveRefAudio,
            onDescribe: callbacksRef?.current?.onDescribe,
            onDescribeComplete: callbacksRef?.current?.onDescribeComplete,
            getMjDescribeConfig: () => callbacksRef?.current?.mjDescribeConfigRef?.current,
            onSlashAssetSpawn,
            onHandleDoubleClick: (e, id) => {
              const sNode = getNodes().find(node => node.id === id);
              setMenuData({
                mouseX: e.clientX + 20, mouseY: e.clientY + 20,
                sourceId: id, sourceType: sNode?.data?.type, sourceNodePos: sNode?.position
              });
            }
          }
        };
      });
      const newEdges = (edges || []).map(e => ({
        ...e,
        id: `edge-wf-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        source: idMap.get(e.source) || e.source,
        target: idMap.get(e.target) || e.target,
      }));
      setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), ...newNodes]);
      setFlowEdges(eds => [...eds, ...newEdges]);
      setAlertMsg({ text: `已添加工作流 "${asset.title}"`, type: "success" });

      // 🛡️ 异步解析 local-cache:// 节点的本地缓存 → blob URL
      const crewId = crewIdRef.current;
      const crewName = crewNameRef.current;
      if (crewId) {
        const localCacheNodes = newNodes.filter(n => n.data?.output?.startsWith('local-cache://'));
        if (localCacheNodes.length > 0) {
          Promise.all(localCacheNodes.map(async (n) => {
            try {
              const fileType = n.data.type === 'video' ? 'video' : 'image';
              const result = await localCacheService.getLocalUrlOrFetch(n.data.output, crewId, crewName, fileType);
              if (result?.url) {
                handleUpdateNode(n.id, { localCacheUrl: result.url, uploading: false });
              }
            } catch (e) { /* 缓存解析失败不影响使用 */ }
          }));
        }
      }
      return;
    }

    // 普通资产
    const id = `node-${Date.now()}`;
    const position = getSmartSpawnPosition('right');
    let customSize = asset.type === 'audio'
      ? { width: 160, height: 80 }
      : (asset.type === 'text' ? { width: 240, height: 150 } : { width: 240, height: 240 });

    if (asset.type === 'image') {
      const img = new Image();
      img.onload = () => handleUpdateNode(id, { customSize: calculateLongSide120(img.width, img.height) });
      img.src = asset.url;
    } else if (asset.type === 'video') {
      const vid = document.createElement('video');
      vid.onloadedmetadata = () => handleUpdateNode(id, { customSize: calculateLongSide120(vid.videoWidth, vid.videoHeight) });
      vid.src = asset.url;
    }

    const newNode = {
      id, type: 'aiNode', position, selected: true,
      data: {
        id, type: asset.type, title: asset.title,
        description: asset.description, // 🔧 描述词字段
        isDirectUpload: true, output: asset.url, outputs: asset.url ? [asset.url] : [],
        thumbnail: asset.thumbnail_url, useLoadingEffect: true,
        // 🔒 加密资产：注入锁定标记
        isSystemSpell: (asset.type === 'text' || asset.type === 'workflow') ? !!asset.isEncrypted : undefined,
        prompt: asset.type === 'text' ? (asset.text || asset.url) : undefined,
        text: asset.type === 'text' ? (asset.text || asset.url) : undefined,
        bgColor: asset.bgColor || (asset.type === 'text' ? getRandomTextBg() : undefined),
        // ❌ 移除 assetId，资产库是模板不是引用
        refFiles: [], refTexts: [], refAudios: [], batchCount: 1, customSize,
        onUpdate: handleUpdateNode, onDelete: handleDeleteNodeButton,
        onDetach: handleDetachImage, onDownscaleInPlace: (...args) => callbacksRef?.current?.onDownscaleInPlace?.(...args), onReupload: handleReuploadLogic,
        onReplace: handleReplace, onGenerate: handleGenerate,
        onRemoveRefFile: handleRemoveRefFile, onRemoveRefText: handleRemoveRefText,
        onRemoveRefAudio: handleRemoveRefAudio,
        onDescribe: callbacksRef?.current?.onDescribe,
        onDescribeComplete: callbacksRef?.current?.onDescribeComplete,
        getMjDescribeConfig: () => callbacksRef?.current?.mjDescribeConfigRef?.current,
        onSlashAssetSpawn,
        onHandleDoubleClick: (e, id) => {
          const sNode = getNodes().find(n => n.id === id);
          setMenuData({
            mouseX: e.clientX + 20, mouseY: e.clientY + 20,
            sourceId: id, sourceType: sNode.data.type, sourceNodePos: sNode.position
          });
        }
      }
    };
    setNodes(nds => [...nds.map(n => ({ ...n, selected: false })), newNode]);
    setAlertMsg({ text: `已添加 "${asset.title || '素材'}" 到图板`, type: "success" });

    // 🛡️ 异步缓存：将 OSS URL 下载到本地缓存，后续渲染走本地避免重复下载
    if (asset.url && asset.url.startsWith('http') && (asset.type === 'image' || asset.type === 'video')) {
      const crewId = crewIdRef.current;
      const crewName = crewNameRef.current;
      if (crewId) {
        const fileType = asset.type === 'video' ? 'video' : 'image';
        localCacheService.getLocalUrlOrFetch(asset.url, crewId, crewName, fileType)
          .then(result => {
            if (result?.url && result.url !== asset.url) {
              handleUpdateNode(id, { localCacheUrl: result.url });
            }
          })
          .catch(() => { /* 缓存失败不影响使用 */ });
      }
    }
    }, [handleUpdateNode, handleDeleteNodeButton, handleDetachImage, handleReuploadLogic, handleGenerate,
      handleRemoveRefFile, handleRemoveRefText, handleRemoveRefAudio, getNodes, getSmartSpawnPosition,
      setNodes, setFlowEdges, setAlertMsg, setMenuData, handleReplace, screenToFlowPosition,
      crewIdRef, crewNameRef, onSlashAssetSpawn, callbacksRef]);

  // --- 删除项目资产 ---
  const handleDeleteAsset = useCallback((assetId) => {
    setGlobalAssets(prev => {
      const target = prev.find(a => a.id === assetId);
      if (target) {
        revokeSecureBlob(target.url);
        if (target.thumbnailUrl && target.thumbnailUrl !== target.url) revokeSecureBlob(target.thumbnailUrl);
      }
      return prev.filter(a => a.id !== assetId);
    });
    api.delete(`/api/assets/${assetId}`).catch(err => console.error("删除失败:", err));
  }, [revokeSecureBlob, setGlobalAssets]);

  // --- 更新资产标题 ---
  const handleUpdateAsset = useCallback((assetId, newTitle) => {
    setGlobalAssets(prev => prev.map(a => a.id === assetId ? { ...a, title: newTitle } : a));
    setPublicAssets(prev => prev.map(a => a.id === assetId ? { ...a, title: newTitle } : a));
    api.put(`/api/assets/${assetId}`, { title: newTitle }).catch(err => {
      console.error("更新资产失败:", err);
      setAlertMsg({ text: "更新失败", type: "error" });
    });
  }, [setGlobalAssets, setPublicAssets, setAlertMsg]);

  // --- 更新资产描述词 ---
  const handleUpdateAssetDescription = useCallback((assetId, newDescription) => {
    setGlobalAssets(prev => prev.map(a => a.id === assetId ? { ...a, description: newDescription } : a));
    setPublicAssets(prev => prev.map(a => a.id === assetId ? { ...a, description: newDescription } : a));
    api.put(`/api/assets/${assetId}`, { description: newDescription }).catch(err => {
      console.error("更新资产描述词失败:", err);
      setAlertMsg({ text: "描述词更新失败", type: "error" });
    });
  }, [setGlobalAssets, setPublicAssets, setAlertMsg]);

  // --- 删除公共素材 ---
  const handleDeletePublicAsset = useCallback((assetId) => {
    setPublicAssets(prev => {
      const target = prev.find(a => a.id === assetId);
      if (target) {
        revokeSecureBlob(target.url);
        if (target.thumbnailUrl && target.thumbnailUrl !== target.url) revokeSecureBlob(target.thumbnailUrl);
      }
      return prev.filter(a => a.id !== assetId);
    });
    api.delete(`/api/assets/${assetId}`)
      .then(() => setAlertMsg({ text: "公共素材已移除", type: "success" }))
      .catch(err => console.error("删除公共素材失败:", err));
  }, [revokeSecureBlob, setPublicAssets, setAlertMsg]);

  // --- 删除全局咒语 ---
  const handleDeleteGlobalAsset = useCallback((assetId) => {
    setGlobalSpells(prev => {
      const target = prev.find(a => a.id === assetId);
      if (target) {
        revokeSecureBlob(target.url);
        if (target.thumbnailUrl && target.thumbnailUrl !== target.url) revokeSecureBlob(target.thumbnailUrl);
      }
      return prev.filter(a => a.id !== assetId);
    });
    api.delete(`/api/assets/${assetId}`)
      .then(() => setAlertMsg({ text: "全局咒语已移除", type: "success" }))
      .catch(err => console.error("删除全局咒语失败:", err));
  }, [revokeSecureBlob, setGlobalSpells, setAlertMsg]);

  // --- 保存到后台 ---
  const handleCommitSave = useCallback(async (assetInfo) => {
    if (!saveModalData || !saveModalData.nodeData) {
      return;
    }

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // 🆕 编辑资产模式：更新已有资产
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    if (assetInfo.isEditing && assetInfo.assetId) {
      try {
        let finalUrl = null;
        let finalThumbnailUrl = null;
        let finalMiniThumbnailUrl = null;
        let imageReplaced = false; // 标记是否替换了图片

        // 如果替换了图片，需要上传新图片和缩略图
        if (assetInfo.newImageFile) {
          imageReplaced = true;

          // 上传主文件
          const mainFormData = new FormData();
          mainFormData.append('file', assetInfo.newImageFile);
          const mainData = await api.upload('/api/upload', mainFormData);
          finalUrl = mainData.url;

          // 上传标准缩略图
          if (assetInfo.thumbnailBlob) {
            const tFile = new File([assetInfo.thumbnailBlob], `thumb-${Date.now()}.png`, { type: 'image/png' });
            const tFormData = new FormData();
            tFormData.append('file', tFile);
            const tData = await api.upload('/api/upload?type=thumbnail', tFormData);
            finalThumbnailUrl = tData.url;
          }

          // 上传迷你缩略图
          if (assetInfo.miniThumbnailBlob) {
            const miniFile = new File([assetInfo.miniThumbnailBlob], `mini-thumb-${Date.now()}.jpg`, { type: 'image/jpeg' });
            const miniFormData = new FormData();
            miniFormData.append('file', miniFile);
            const miniData = await api.upload('/api/upload?type=thumbnail', miniFormData);
            finalMiniThumbnailUrl = miniData.url;
          }

          // 更新数据库
          await api.put(`/api/assets/${assetInfo.assetId}`, {
            title: assetInfo.title,
            description: assetInfo.description,
            url: finalUrl,
            thumbnail_url: finalThumbnailUrl,
            mini_thumbnail_url: finalMiniThumbnailUrl,
          });

          // 更新本地状态
          setGlobalAssets(prev => prev.map(a =>
            a.id === assetInfo.assetId ? {
              ...a,
              title: assetInfo.title,
              description: assetInfo.description,
              url: finalUrl,
              thumbnailUrl: finalThumbnailUrl,
              miniThumbnailUrl: finalMiniThumbnailUrl,
              // 🛡️ 失效旧的 localCacheUrl，让系统重新缓存
              localCacheUrl: undefined,
              localThumbUrl: URL.createObjectURL(assetInfo.thumbnailBlob),
            } : a
          ));

          // 🔄 后台写入新缓存（主文件和缩略图）
          const currentCrewId = crewIdRef.current;
          const currentCrewName = crewNameRef.current;
          if (currentCrewId && finalUrl) {
            const fileType = saveModalData.nodeData.type === 'video' ? 'video' : 'image';
            localCacheService.getLocalUrlOrFetch(finalUrl, currentCrewId, currentCrewName, fileType)
              .catch(() => {});
          }
          if (currentCrewId && finalThumbnailUrl) {
            localCacheService.getLocalUrlOrFetch(finalThumbnailUrl, currentCrewId, currentCrewName, 'image')
              .catch(() => {});
          }
        } else {
          // 没有替换图片，只更新标题和描述
          await api.put(`/api/assets/${assetInfo.assetId}`, {
            title: assetInfo.title,
            description: assetInfo.description,
          });

          setGlobalAssets(prev => prev.map(a =>
            a.id === assetInfo.assetId ? {
              ...a,
              title: assetInfo.title,
              description: assetInfo.description,
            } : a
          ));
        }

        // ✅ 更新完成，关闭面板
        setAlertMsg({ text: "资产已更新", type: "success" });
        setSaveModalData(null);
        return;
      } catch (err) {
        console.error("资产更新失败:", err);
        setAlertMsg({ text: "资产更新失败", type: "error" });
        return;
      }
    }

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // 🆕 编辑封面模式：只更新现有资产的封面，不创建新资产
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    if (saveModalData.nodeData.isEditingCover) {
      const assetId = saveModalData.nodeData.assetId;
      const assetScope = saveModalData.nodeData.assetScope || 'project';

      if (!assetId) {
        setAlertMsg({ text: "资产ID缺失，无法更新封面", type: "error" });
        return;
      }

      try {
        let finalThumbnailUrl = null;

        // 上传新的缩略图
        if (assetInfo.thumbnailBlob) {
          const tFile = new File([assetInfo.thumbnailBlob], `thumb-${Date.now()}.png`, { type: 'image/png' });
          const tFormData = new FormData();
          tFormData.append('file', tFile);
          const tData = await api.upload('/api/upload?type=thumbnail', tFormData);
          finalThumbnailUrl = tData.url;
        }

        if (!finalThumbnailUrl) {
          setAlertMsg({ text: "请先选择并裁剪封面图片", type: "warning" });
          return;
        }

        // 更新资产的 thumbnail_url
        await api.put(`/api/assets/${assetId}`, {
          thumbnail_url: finalThumbnailUrl
        });

        // 更新本地状态
        const updateAssetInList = (prev) => prev.map(a =>
          a.id === assetId ? { ...a, thumbnailUrl: finalThumbnailUrl, localThumbUrl: URL.createObjectURL(assetInfo.thumbnailBlob) } : a
        );

        // 根据 assetScope 更新对应的状态
        // 'global' → globalSpells（全局库）
        // 'public' → publicAssets（团队库）
        // 'personal' → globalAssets（个人库）
        if (assetScope === 'global') {
          setGlobalSpells(updateAssetInList);
        } else if (assetScope === 'public') {
          setPublicAssets(updateAssetInList);
        } else {
          setGlobalAssets(updateAssetInList); // personal
        }

        setAlertMsg({ text: "封面已更新", type: "success" });
        setSaveModalData(null);
        return;
      } catch (err) {
        console.error("封面更新失败:", err);
        setAlertMsg({ text: "封面更新失败", type: "error" });
        return;
      }
    }

    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    // 🛡️ 重复检测（全局咒语和普通资产互不干扰，允许同内容双份保存）
    // ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
    const savingAsGlobal = !!assetInfo.isGlobal;
    // 全局咒语进 globalSpells，普通资产进 globalAssets，各查各的
    const targetAssetList = savingAsGlobal ? globalSpellsRef.current : globalAssetsRef.current;

    // 重复检测 1：assetId 已存在于目标列表
    const liveNode = getNodes().find(n => n.id === saveModalData.nodeData.id);
    const liveAssetId = liveNode?.data?.assetId || saveModalData.nodeData.assetId;
    if (!savingAsGlobal && liveAssetId && targetAssetList.some(a => a.id === liveAssetId)) {
      setSaveModalData(null); // 先关闭弹窗
      setTimeout(() => {
        setAlertMsg({ text: "该素材已在后台中", type: "warning" });
      }, 100); // 延迟显示提示，确保模态框已关闭
      return;
    }

    // 重复检测 2：按内容匹配（仅在同类型列表中查重）
    const isTextNode = saveModalData.nodeData.type === 'text' || assetInfo.type === 'text';
    if (isTextNode) {
      const textContent = saveModalData.nodeData.prompt || saveModalData.nodeData.text;
      if (textContent && targetAssetList.some(a => a.type === 'text' && a.text === textContent)) {
        setAlertMsg({ text: savingAsGlobal ? "内容重复：该全局咒语已存在" : "内容重复：该咒语已存在后台", type: "warning" });
        return;
      }
    } else {
      const outputUrl = saveModalData.nodeData.output;
      if (outputUrl) {
        const outputFileName = outputUrl.match(/([^/]+)$/)?.[1];
        const isDuplicate = targetAssetList.some(a => {
          if (a.url === outputUrl) return true;
          if (outputFileName && a.url && a.url.endsWith(outputFileName)) return true;
          return false;
        });
        if (isDuplicate) {
          setAlertMsg({ text: "内容重复：该素材已存在", type: "warning" });
          return;
        }
      }
    }

    const originalUrl = saveModalData.nodeData.output;
    let finalUrl = originalUrl;
    let finalThumbnailUrl = originalUrl;
    let needsBackgroundUpload = false; // 标记是否需要后台异步上传主文件

    try {
      finalUrl = originalUrl;
      finalThumbnailUrl = assetInfo.thumbnailUrl || originalUrl;

      if (originalUrl && originalUrl.startsWith('blob:')) {
        const response = await fetch(originalUrl);
        const blob = await response.blob();
        const ext = blob.type.split('/')[1] || 'png';
        const file = new File([blob], `asset-main-${Date.now()}.${ext}`, { type: blob.type });
        const upData = await api.presignUpload(file);
        finalUrl = upData.url;
      } else if (originalUrl && originalUrl.startsWith('local-cache://')) {
        // ✅ local-cache:// 优先入库：保留 local-cache URL，不阻塞用户操作
        // 主文件上传放到后台异步完成，先用 local-cache:// URL 保存到数据库
        finalUrl = originalUrl;
        needsBackgroundUpload = true;
      }

      // 🆕 上传标准缩略图（768px）
      if (assetInfo.thumbnailBlob) {
        const tFile = new File([assetInfo.thumbnailBlob], `thumb-${Date.now()}.png`, { type: 'image/png' });
        const tFormData = new FormData();
        tFormData.append('file', tFile);
        const tData = await api.upload('/api/upload?type=thumbnail', tFormData);
        finalThumbnailUrl = tData.url;
      } else if (finalUrl !== originalUrl) {
        finalThumbnailUrl = finalUrl;
      } else if (originalUrl && !originalUrl.startsWith('blob:') && !assetInfo.thumbnailBlob) {
        finalThumbnailUrl = originalUrl;
      }

      // 🆕 上传迷你缩略图（80px）
      let finalMiniThumbnailUrl = null;
      if (assetInfo.miniThumbnailBlob) {
        const miniFile = new File([assetInfo.miniThumbnailBlob], `mini-thumb-${Date.now()}.jpg`, { type: 'image/jpeg' });
        const miniFormData = new FormData();
        miniFormData.append('file', miniFile);
        const miniData = await api.upload('/api/upload?type=thumbnail', miniFormData);
        finalMiniThumbnailUrl = miniData.url;
      }

      // 判断是否继承系统咒语的锁定状态（非 super_admin 保存系统咒语节点时）
      const nodeIsSystemSpell = !!saveModalData.nodeData.isSystemSpell;
      const payload = {
        projectId: savingAsGlobal ? undefined : (projectId || undefined),
        isPublic: false,
        type: assetInfo.type,
        title: assetInfo.title,
        description: assetInfo.description, // 🆕 描述词
        category: assetInfo.category,
        url: finalUrl,
        thumbnail_url: finalThumbnailUrl,
        mini_thumbnail_url: finalMiniThumbnailUrl, // 🆕 迷你缩略图
        is_global: !!assetInfo.isGlobal,
        is_encrypted: !!assetInfo.isEncrypted,
        inherit_encrypted: nodeIsSystemSpell && !assetInfo.isGlobal, // 保存到后台时继承加密锁定
        data: assetInfo.type === 'workflow'
          ? (() => {
              // 🛡️ 清理工作流节点中的运行态字段和临时 URL，避免序列化时出现循环引用或携带不可持久化数据
              const raw = saveModalData.nodeData.data;
              if (raw && Array.isArray(raw.nodes)) {
                return {
                  ...raw,
                  nodes: raw.nodes.map(n => {
                    const { localCacheUrl, uploading, annotationPreview, ...cleanData } = n.data || {};

                    // 清理非合法持久化 URL（blob/data/裸 UUID 等）
                    const isValidPersistUrl = (u) => {
                      if (!u) return false;
                      // 接受：绝对 HTTP(S)、local-cache://、以 / 开头的站点相对路径
                      if (u.startsWith('http') || u.startsWith('local-cache://') || u.startsWith('/')) return true;
                      // 兼容：部分后端/工具会返回不带前导斜杠的相对上传路径（如 "uploads/xxx.png" 或 "thumbnails/yyy.jpg"）
                      if (u.startsWith('uploads/') || u.startsWith('thumbnails/') || u.includes('/uploads/') || u.includes('/thumbnails/')) return true;
                      return false;
                    };
                    if (cleanData.output && !isValidPersistUrl(cleanData.output)) {
                      cleanData.output = undefined;
                    }
                    if (Array.isArray(cleanData.outputs)) {
                      cleanData.outputs = cleanData.outputs.filter(u => isValidPersistUrl(u));
                      if (cleanData.outputs.length === 0) cleanData.outputs = undefined;
                    }

                    // 清理引用数组中的 localCacheUrl 字段
                    if (cleanData.refFiles) cleanData.refFiles = cleanData.refFiles.map(({ localCacheUrl: _, ...f }) => f);
                    if (cleanData.refAudios) cleanData.refAudios = cleanData.refAudios.map(({ localCacheUrl: _, ...a }) => a);

                    // 明确移除已知的运行态回调 / refs / 工具属性，防止循环引用
                    const runtimeKeys = [
                      'onUpdate','onDelete','onGenerate','onDetach','onReupload','onReplace','onUploadAndReplace',
                      'onRemoveRefFile','onRemoveRefText','onRemoveRefAudio','onSlashAssetSpawn','onHandleDoubleClick',
                      'onDisconnectDownstream','onDescribe','onDescribeComplete','onMjAction','onGptInpaint',
                      'getMjDescribeConfig','getMjToolboxConfig','getGptInpaintConfig',
                      'globalAssetsRef','publicAssetsRef','globalSpellsRef','mjToolboxConfigRef','mjDescribeConfigRef'
                    ];
                    for (const k of runtimeKeys) delete cleanData[k];

                    // 额外防护：删除所有 function 类型字段和所有形似 React ref 的对象（包含 current 属性）
                    for (const key of Object.keys(cleanData)) {
                      try {
                        const v = cleanData[key];
                        if (typeof v === 'function') {
                          delete cleanData[key];
                          continue;
                        }
                        if (v && typeof v === 'object' && Object.prototype.hasOwnProperty.call(v, 'current')) {
                          delete cleanData[key];
                          continue;
                        }
                      } catch (e) {
                        // 如果检查字段抛错，保守删除该字段以避免序列化问题
                        delete cleanData[key];
                      }
                    }

                    return { ...n, data: cleanData };
                  })
                };
              }
              return raw;
            })()
          : {
              bgColor: assetInfo.bgColor || saveModalData.nodeData.bgColor,
              text: assetInfo.type === 'text' ? (saveModalData.nodeData.prompt || saveModalData.nodeData.text) : undefined
            }
      };

      const savedAsset = await api.post('/api/assets', payload);
      const normalizedSavedAsset = normalizeAsset(savedAsset);

      // ✅ 立即设置本地缓存 URL，避免 AssetDrawer 走远程下载
      // 复用节点已有的 blob URL 作为素材的 localCacheUrl
      if (saveModalData.nodeData.localCacheUrl) {
        normalizedSavedAsset.localCacheUrl = saveModalData.nodeData.localCacheUrl;
      }
      // 缩略图刚在前端裁剪生成，直接创建 blob URL 供即时显示
      if (assetInfo.thumbnailBlob) {
        normalizedSavedAsset.localThumbUrl = URL.createObjectURL(assetInfo.thumbnailBlob);
      }

      // 后台异步写入三层缓存（保证下次加载也命中本地）
      if (finalUrl && !finalUrl.startsWith('local-cache://') && crewIdRef.current) {
        localCacheService.getLocalUrlOrFetch(finalUrl, crewIdRef.current, crewNameRef.current, assetInfo.type)
          .catch(() => {});
      }
      if (finalThumbnailUrl && finalThumbnailUrl !== finalUrl && crewIdRef.current) {
        localCacheService.getLocalUrlOrFetch(finalThumbnailUrl, crewIdRef.current, crewNameRef.current, 'image')
          .catch(() => {});
      }

      // 🌍 全局咒语（super_admin 主动发布）进入全局列表
      if (assetInfo.isGlobal) {
        setGlobalSpells(prev => prependUniqueAsset(prev, normalizedSavedAsset));
      } else {
        setGlobalAssets(prev => prependUniqueAsset(prev, normalizedSavedAsset));
      }

      // 全局发布不写 assetId 到节点（允许同一节点再保存为普通资产）
      // 继承加密咒语的也不写（节点本身已有 isSystemSpell 标记）
      if (assetInfo.type !== 'workflow' && !assetInfo.isGlobal && !nodeIsSystemSpell) {
        handleUpdateNode(saveModalData.nodeData.id, { assetId: savedAsset.id });
      }

      setDrawerOpen(true);
      setActiveTab(assetInfo.isGlobal ? 'global' : 'project');
      setAlertMsg({ text: assetInfo.isGlobal ? (assetInfo.isEncrypted ? "全局加密咒语已保存" : "全局咒语已保存") : nodeIsSystemSpell ? "加密咒语已保存到后台" : "资产及封面已保存", type: "success" });
      setSaveModalData(null);

      // 🔄 后台异步上传：local-cache:// 主文件上传到 OSS，完成后回填资产 URL
      if (needsBackgroundUpload && savedAsset.id) {
        (async () => {
          try {
            const match = originalUrl.match(/^local-cache:\/\/([^/]+)\/([^/]+)\/(.+)$/);
            if (match) {
              const [, cachedCrewId, typeFolder, fileName] = match;
              const blob = await localCacheService.readFileFromCache(
                cachedCrewId, crewNameRef.current, typeFolder, fileName
              );
              if (blob) {
                const ext = fileName.split('.').pop() || 'png';
                const mimeType = typeFolder === 'videos' ? 'video/' + ext
                  : typeFolder === 'audios' ? 'audio/' + ext : 'image/' + ext;
                const file = new File([blob], `asset-bg-${Date.now()}.${ext}`, { type: mimeType });
                const upData = await api.presignUpload(file);
                // 回填 OSS URL 到数据库
                await api.put(`/api/assets/${savedAsset.id}`, {
                  title: assetInfo.title, url: upData.url
                });
                // 同步更新本地 globalAssets 列表中的 URL
                setGlobalAssets(prev => prev.map(a =>
                  a.id === savedAsset.id ? { ...a, url: upData.url } : a
                ));
                console.log(`[Asset ${savedAsset.id}] 后台上传完成: ${upData.url}`);
              }
            }
          } catch (e) {
            console.error(`[Asset ${savedAsset.id}] 后台上传失败，将在下次加载时重试:`, e);
          }
        })();
      }
    } catch (err) {
      console.error("资产保存失败:", err);
      setAlertMsg({ text: "资产保存失败: 数据处理错误", type: "error" });
    }
  }, [saveModalData, globalAssetsRef, publicAssetsRef, globalSpellsRef, projectId, handleUpdateNode, setGlobalAssets, setPublicAssets, setGlobalSpells,
      setDrawerOpen, setActiveTab, setAlertMsg, setSaveModalData, getNodes]);

  // --- 上传到公共库 ---
  const handleUploadToPublic = useCallback(async (assetIds) => {
    const assetIdStrings = assetIds.map(id => String(id));
    const assetsToUpload = globalAssetsRef.current.filter(a => assetIdStrings.includes(String(a.id)));

    if (assetsToUpload.length === 0) {
      setAlertMsg({ text: "未找到要上传的资产", type: "warning" });
      return;
    }

    let successCount = 0;
    for (const asset of assetsToUpload) {
      let alreadyInPublic = false;
      if (asset.type === 'text') {
        alreadyInPublic = publicAssetsRef.current.some(p => p.type === 'text' && p.text === asset.text);
      } else if (asset.type === 'workflow') {
        alreadyInPublic = false;
      } else {
        alreadyInPublic = publicAssetsRef.current.some(p => p.url === asset.url);
      }
      if (alreadyInPublic) continue;

      try {
        const payload = {
          type: asset.type, title: asset.title, category: asset.category,
          url: asset.url, thumbnail_url: asset.thumbnailUrl || asset.thumbnail_url,
          description: asset.description || '', // 🆕 传递描述词到剧组库
          data: asset.data, projectId: null, isPublic: true,
          // 保留加密锁定状态（如来自全局加密咒语）
          is_encrypted: !!asset.isEncrypted,
          inherit_encrypted: !!asset.isEncrypted,
        };
        const savedAsset = await api.post('/api/assets', payload);
        const normalizedSaved = normalizeAsset(savedAsset);
        const mergedPublic = prependUniqueAsset(publicAssetsRef.current, normalizedSaved);
        setPublicAssets(mergedPublic);
        publicAssetsRef.current = mergedPublic;
        successCount++;
      } catch (err) {
        console.error("Export to public failed", err);
      }
    }

    if (successCount > 0) {
      setAlertMsg({ text: `成功同步 ${successCount} 个资产到公共库`, type: "success" });
    } else {
      setAlertMsg({ text: "所选资产已存在于公共库或上传失败", type: "info" });
    }
  }, [globalAssetsRef, publicAssetsRef, setPublicAssets, setAlertMsg]);

  // --- 从公共库下载到项目 ---
  const handleDownloadFromPublic = useCallback(async (assetIds, sourceTab = 'public') => {
    const assetIdStrings = assetIds.map(id => String(id));
    const sourceAssets = sourceTab === 'global' ? globalSpellsRef.current : publicAssetsRef.current;
    const assetsToDownload = sourceAssets.filter(a => assetIdStrings.includes(String(a.id)));

    if (assetsToDownload.length === 0) {
      setAlertMsg({ text: "未找到要下载的资产", type: "warning" });
      return;
    }

    // 重复检查：按类型判重
    const newLocals = assetsToDownload.filter(a => {
      // 加密资产按 title+type 判重
      if (a.isEncrypted) {
        return !globalAssetsRef.current.some(l => l.isEncrypted && l.title === a.title && l.type === a.type);
      }
      // 文本资产按内容判重（url 可能为空）
      if (a.type === 'text') {
        const incomingText = getTextContent(a);
        if (!incomingText) return true; // 空文本允许下载
        return !globalAssetsRef.current.some(l => {
          if (l.type !== 'text') return false;
          const existingText = getTextContent(l);
          return existingText === incomingText;
        });
      }
      // 其他资产按 url 判重
      return !globalAssetsRef.current.some(l => l.url === a.url);
    });

    if (newLocals.length === 0) {
      setAlertMsg({ text: "所选资产已在后台中", type: "warning" });
      return;
    }

    let successCount = 0;
    for (const asset of newLocals) {
      try {
        const payload = {
          projectId, isPublic: false,
          type: asset.type, title: asset.title, category: asset.category,
          url: asset.url, thumbnail_url: asset.thumbnailUrl || asset.thumbnail_url,
          description: asset.description || '', // 🆕 传递描述词到个人库
          data: asset.data,
          // 加密资产下载到后台时继承锁定状态
          inherit_encrypted: !!asset.isEncrypted,
        };
        const savedAsset = await api.post('/api/assets', payload);
        setGlobalAssets(prev => prependUniqueAsset(prev, normalizeAsset(savedAsset)));
        successCount++;

        const currentCrewId = crewIdRef.current;
        const currentCrewName = crewNameRef.current;
        if (currentCrewId && asset.url && !asset.url.startsWith('blob:') && !asset.url.startsWith('data:')) {
          const fileType = asset.category === 'videos' ? 'video'
            : asset.category === 'audios' ? 'audio' : 'image';
          localCacheService.getLocalUrlOrFetch(asset.url, currentCrewId, currentCrewName, fileType)
            .catch(err => console.warn('后台缓存写入失败:', err));
        }
      } catch (err) {
        console.error("Import from public failed", err);
      }
    }

    if (successCount > 0) {
      setAlertMsg({ text: `成功保存 ${successCount} 个资产到项目`, type: "success" });
    }
  }, [projectId, globalAssetsRef, publicAssetsRef, globalSpellsRef, crewIdRef, crewNameRef, setGlobalAssets, setAlertMsg, prependUniqueAsset]);

  // --- 打开保存弹窗 ---
  const handleOpenSaveModal = useCallback(() => {
    const contextMenu = deps.contextMenu;
    if (!contextMenu || !contextMenu.targetNodeId) return;
    const targetNode = getNodes().find(n => n.id === contextMenu.targetNodeId);
    if (targetNode) {
      setSaveModalData({ isOpen: true, nodeData: targetNode.data });
      setContextMenu(null);
    }
  }, [deps.contextMenu, getNodes, setSaveModalData, setContextMenu]);

  // --- 设置资产封面（重新编辑已保存资产的封面）---
  const handleSetAssetCover = useCallback((asset) => {
    // 根据资产属性推断 scope（后端没有 scope 字段）
    let assetScope = 'personal'; // 默认个人库
    if (asset.is_global || asset.isGlobal) {
      assetScope = 'global'; // 全局库
    } else if (asset.is_public || asset.isPublic) {
      assetScope = 'public'; // 团队库
    }

    // 将资产转换为类似节点数据的格式，以便复用 SaveAssetModal
    const nodeData = {
      id: `asset-${asset.id}`, // 临时 ID，用于区分
      type: asset.type,
      title: asset.title,
      category: asset.category,
      // 添加编辑标记和原始资产 ID
      isEditingCover: true,
      assetId: asset.id,
      assetScope, // personal/public/global（根据 is_global/is_public 推断）
      // 传递现有的封面URL（如果有），供预览
      thumbnailUrl: asset.thumbnailUrl || asset.thumbnail_url,
      // 如果是工作流，需要传递 data
      ...(asset.type === 'workflow' && asset.data ? { data: asset.data } : {}),
    };
    setSaveModalData({ isOpen: true, nodeData });
  }, [setSaveModalData]);

  // 🆕 编辑资产（重新编辑已保存资产的标题、描述和图片）
  const handleEditAsset = useCallback((asset) => {
    // 将资产转换为节点数据格式
    const nodeData = {
      id: `asset-${asset.id}`,
      type: asset.type,
      title: asset.title,
      category: asset.category,
      description: asset.description || '',
      // 🆕 编辑资产标记
      isEditingAsset: true,
      editingAssetId: asset.id,
      // 传递资产URL用于预览和裁剪
      url: asset.url,
      output: asset.url,
      localCacheUrl: asset.localCacheUrl,
      thumbnailUrl: asset.thumbnailUrl || asset.thumbnail_url,
      // 如果是工作流，传递 data
      ...(asset.type === 'workflow' && asset.data ? { data: asset.data } : {}),
    };
    setSaveModalData({ isOpen: true, nodeData });
  }, [setSaveModalData]);

  return {
    handleSpawnAsset, handleDeleteAsset, handleUpdateAsset, handleUpdateAssetDescription, handleDeletePublicAsset, handleDeleteGlobalAsset,
    handleCommitSave, handleUploadToPublic, handleDownloadFromPublic, handleOpenSaveModal,
    handleSetAssetCover,
    handleEditAsset, // 🆕 导出编辑资产回调
  };
};

export default useAssetManagement;
